import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { fetchFeatureFlags } from "../api/FeatureFlagApi";

const DEFAULT_COUNTRY_CODE = "USA";

const FeatureFlagContext = createContext({
	flags: {},
	evaluateFlag: () => ({ enabled: false, meta: {} }),
	isFeatureEnabled: () => false,
	refreshFlags: () => Promise.resolve(),
	loading: true,
	error: null,
	countryCode: DEFAULT_COUNTRY_CODE,
});

const normaliseCountryCode = (value) => {
	if (!value && value !== 0) {
		return null;
	}
	return value.toString().trim().toUpperCase();
};

const extractCountryFromLocale = (locale) => {
	if (!locale || typeof locale !== "string") {
		return null;
	}
	const normalised = locale.replace(/_/g, "-");
	const match = normalised.match(/-(?<country>[A-Za-z]{2,3})$/);
	if (match && match.groups?.country) {
		return normaliseCountryCode(match.groups.country);
	}
	return null;
};

const deriveCountryCode = () => {
	if (typeof window !== "undefined") {
		try {
			const storedCountry = window.localStorage?.getItem("countryCode");
			if (storedCountry) {
				return normaliseCountryCode(storedCountry);
			}
		} catch (err) {
			// Ignore storage errors (e.g. Safari private mode).
		}
	}

	const navigatorLocales =
		typeof navigator !== "undefined"
			? [
					...(navigator.languages || []),
					navigator.language,
					navigator.userLanguage,
					navigator.browserLanguage,
					navigator.systemLanguage,
			  ].filter(Boolean)
			: [];

	for (const locale of navigatorLocales) {
		const country = extractCountryFromLocale(locale);
		if (country) {
			return country;
		}
	}

	try {
		const intlLocale = Intl.DateTimeFormat().resolvedOptions().locale;
		const country = extractCountryFromLocale(intlLocale);
		if (country) {
			return country;
		}
	} catch (err) {
		// Ignore environments without Intl support.
	}

	return DEFAULT_COUNTRY_CODE;
};

const parseCountryList = (values) => {
	if (values === undefined || values === null) {
		return null;
	}

	const entries = Array.isArray(values) ? values : [values];

	const flattened = entries.flatMap((value) => {
		if (value === undefined || value === null) {
			return [];
		}

		if (Array.isArray(value)) {
			return value;
		}

		if (typeof value === "string") {
			return value.split(",");
		}

		return [value];
	});

	return flattened.map((value) => normaliseCountryCode(value)).filter(Boolean);
};

const evaluateCountryFilter = (filter, context, meta) => {
	const parameters = filter?.parameters || {};

	const disallowedCountries = parseCountryList(
		parameters.DisallowedCountries ||
			parameters.disallowedCountries ||
			parameters.ExcludedCountries ||
			parameters.excludedCountries ||
			parameters.blockedCountries ||
			parameters.BlockedCountries
	);

	const allowedCountries = parseCountryList(
		parameters.AllowedCountries ||
			parameters.allowedCountries ||
			parameters.countries ||
			parameters.CountryCodes ||
			parameters.countryCodes
	);

	meta.filters.push({
		type: "CountryFilter",
		allowedCountries: allowedCountries ?? [],
		disallowedCountries: disallowedCountries ?? [],
		matchedCountry: context.countryCode,
	});

	const normalisedCountry = normaliseCountryCode(context.countryCode);

	if (Array.isArray(disallowedCountries)) {
		if (!normalisedCountry) {
			return true;
		}
		return !disallowedCountries.includes(normalisedCountry);
	}

	if (!Array.isArray(allowedCountries) || allowedCountries.length === 0) {
		return false;
	}

	if (!normalisedCountry) {
		return false;
	}

	return allowedCountries.includes(normalisedCountry);
};

const evaluateFilter = (filter, context, meta) => {
	if (!filter || typeof filter !== "object") {
		return true;
	}

	const filterName = filter.name || "Unknown";

	switch (filterName) {
		case "CountryFilter":
		case "country":
		case "country_filter":
			return evaluateCountryFilter(filter, context, meta);
		default:
			meta.filters.push({
				type: filterName,
				unsupported: true,
			});
			return false;
	}
};

const evaluateConditions = (flag, context, meta) => {
	const filters = flag?.conditions?.client_filters;
	if (!Array.isArray(filters) || filters.length === 0) {
		return true;
}

	return filters.every((filter) => evaluateFilter(filter, context, meta));
};

const evaluateFlagValue = (flagValue, defaultValue, context) => {
	const meta = {
		filters: [],
		countryCode: context.countryCode,
		missing: false,
	};

	if (typeof flagValue === "boolean") {
		return { enabled: flagValue, meta };
	}

	if (!flagValue || typeof flagValue !== "object") {
		meta.missing = true;
		return { enabled: defaultValue, meta };
	}

	const baseEnabled =
		typeof flagValue.enabled === "boolean" ? flagValue.enabled : defaultValue;

	if (!baseEnabled) {
		return { enabled: false, meta, flag: flagValue };
	}

	const conditionsSatisfied = evaluateConditions(flagValue, context, meta);

	return {
		enabled: baseEnabled && conditionsSatisfied,
		meta,
		flag: flagValue,
	};
};

export const FeatureFlagProvider = ({ children }) => {
	const [flags, setFlags] = useState({});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [countryCode] = useState(() => deriveCountryCode());

	const loadFlags = useCallback(
		async (refresh = true) => {
			setLoading(true);
			setError(null);
			const response = await fetchFeatureFlags({ refresh });
			if (response.success) {
				setFlags(response.flags);
			} else {
				setError(response.error);
				setFlags({});
			}
			setLoading(false);
		},
		[]
	);

	useEffect(() => {
		loadFlags(false);
	}, [loadFlags]);

	const evaluateFlag = useCallback(
		(flagName, defaultValue = false) => {
			const flagValue = flags[flagName];
			return evaluateFlagValue(
				flagValue,
				defaultValue,
				{
					countryCode,
				}
			);
		},
		[flags, countryCode]
	);

	const isFeatureEnabled = useCallback(
		(flagName, defaultValue = false) => evaluateFlag(flagName, defaultValue).enabled,
		[evaluateFlag]
	);

	const refreshFlags = useCallback(() => loadFlags(true), [loadFlags]);

	const value = useMemo(
		() => ({
			flags,
			evaluateFlag,
			isFeatureEnabled,
			refreshFlags,
			loading,
			error,
			countryCode,
		}),
		[flags, evaluateFlag, isFeatureEnabled, refreshFlags, loading, error, countryCode]
	);

	return (
		<FeatureFlagContext.Provider value={value}>
			{children}
		</FeatureFlagContext.Provider>
	);
};

export const useFeatureFlags = () => useContext(FeatureFlagContext);

export const useFeatureFlag = (flagName, defaultValue = false) => {
	const { evaluateFlag, loading } = useFeatureFlags();
	const evaluation = evaluateFlag(flagName, defaultValue);
	return {
		enabled: evaluation.enabled,
		loading,
		evaluation,
	};
};
